/**
 * WallOutletsBulkDialog — alta masiva de tomas de pared en una tabla.
 *
 * POR QUÉ EXISTE
 * Dar de alta 48 tomas de un edificio, ficha a ficha, es media mañana. Aquí se
 * monta la remesa entera —código, sede, patch panel, puerto, switch y sala— y
 * se crea de una vez. Si se elige switch, además del alta se crea el latiguillo
 * panel ↔ switch, de modo que la toma queda conectada «hasta el extremo».
 *
 * CÓMO SE USA, en el orden en que se hace en la vida real
 *   1. Se elige la sede, el patch panel y, si hace falta, uno o varios switches.
 *   2. Prefijo + desde + cuántas → la tabla se rellena sola (T-01, T-02…) y
 *      asigna en orden los puertos LIBRES (panel P1, P2…; switch P1, P2…).
 *   3. Se retoca lo que haga falta o se añaden filas a mano. Los puertos ya
 *      ocupados por otra toma o por un latiguillo no se pueden elegir.
 *   4. Se ordena por la columna que interese y se crea.
 *
 * El backend valida y devuelve los fallos fila a fila (código repetido, puerto
 * ocupado…): se marcan en rojo con su motivo y **el resto se crea igual**, así
 * solo hay que corregir y reintentar lo que quedó mal.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  PlusIcon,
  RectangleStackIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Dialog from './common/Dialog';
import api from '../services/api';
import { OUTLET_TYPES, tipoEtiqueta } from '../config/outletTypes';
import { etiquetaPuerto, motivoOcupadoPanel, puertoPanelLibre } from '../utils/wallOutletGrid';

let secuencia = 0;
const nuevaClave = () => {
  secuencia += 1;
  return `fila-${secuencia}`;
};

const FILA_VACIA = {
  code: '',
  name: '',
  room: '',
  floor: '',
  location_id: '',
  panel_id: '',
  panel_port_id: '',
  switch_id: '',
  switch_port_id: '',
  outlet_type: 'rj45',
  status: 'active',
};

/** Columnas de la tabla, en el orden en que se rellenan. */
const COLUMNAS = [
  { key: 'code',           label: 'Código *' },
  { key: 'name',           label: 'Nombre' },
  { key: 'room',           label: 'Sala' },
  { key: 'floor',          label: 'Planta' },
  { key: 'location_id',    label: 'Sede' },
  { key: 'panel_id',       label: 'Patch panel' },
  { key: 'panel_port_id',  label: 'Puerto panel' },
  { key: 'switch_id',      label: 'Switch' },
  { key: 'switch_port_id', label: 'Puerto switch' },
  { key: 'outlet_type',    label: 'Tipo' },
  { key: 'status',         label: 'Estado' },
];

/** Motivo por el que un puerto de switch ya está pillado (o null). */
function motivoOcupadoSwitch(puerto) {
  if (!puerto) return null;
  if (puerto.connection || puerto.is_connected) {
    const otro = puerto.connection?.other_end || {};
    return otro.element_name ? `latiguillo a ${otro.element_name}` : 'conectado';
  }
  return null;
}

function puertoSwitchLibre(puerto) {
  return Boolean(puerto) && puerto.is_active !== false && !motivoOcupadoSwitch(puerto);
}

function WallOutletsBulkDialog({ companyId, locations = [], onClose, onCreated }) {
  const [panels, setPanels] = useState([]);
  const [switches, setSwitches] = useState([]);
  const [puertos, setPuertos] = useState({});        // id del panel → puertos
  const [puertosSwitch, setPuertosSwitch] = useState({}); // id del switch → puertos
  const [filas, setFilas] = useState(() => [{ clave: nuevaClave(), ...FILA_VACIA }]);
  const [semilla, setSemilla] = useState({
    location_id: '',
    panel_id: '',
    switch_ids: [],
    outlet_type: 'rj45',
    status: 'active',
  });
  const [gen, setGen] = useState({
    prefijo: 'T-', desde: 1, cantidad: 12, digitos: 2,
    panel_desde: 1, switch_desde: 1,
  });
  const [orden, setOrden] = useState(null);     // { key, dir } del último «ordenar por»
  const [fallos, setFallos] = useState({});     // clave de fila → motivo
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');

  useEffect(() => {
    if (!companyId) return;
    api.getElements(companyId, { element_type: 'panel' })
      .then((r) => setPanels(r.data || []))
      .catch(() => setPanels([]));
    api.getElements(companyId, { element_type: 'switch' })
      .then((r) => setSwitches(r.data || []))
      .catch(() => setSwitches([]));
  }, [companyId]);

  /** Los puertos se piden una vez por panel y se recuerdan. */
  const cargarPuertos = async (panelId) => {
    if (!panelId) return [];
    if (puertos[panelId]) return puertos[panelId];
    try {
      const r = await api.getPanelPorts(panelId, companyId);
      const lista = r.data || [];
      setPuertos((prev) => ({ ...prev, [panelId]: lista }));
      return lista;
    } catch {
      setPuertos((prev) => ({ ...prev, [panelId]: [] }));
      return [];
    }
  };

  /** Ídem para switches: la respuesta trae `connection` e `is_connected`. */
  const cargarPuertosSwitch = async (switchId) => {
    if (!switchId) return [];
    if (puertosSwitch[switchId]) return puertosSwitch[switchId];
    try {
      const r = await api.getPorts(switchId, companyId);
      const lista = r.data || [];
      setPuertosSwitch((prev) => ({ ...prev, [switchId]: lista }));
      return lista;
    } catch {
      setPuertosSwitch((prev) => ({ ...prev, [switchId]: [] }));
      return [];
    }
  };

  const nombreSede = (id) => locations.find((l) => String(l.id) === String(id))?.name || '';
  const nombrePanel = (id) => panels.find((p) => String(p.id) === String(id))?.name || '';
  const nombreSwitch = (id) => switches.find((s) => String(s.id) === String(id))?.name || '';
  const panelDeFila = (fila) => panels.find((p) => String(p.id) === String(fila.panel_id));

  const actualizar = (clave, campo, valor) => {
    setFilas((prev) => prev.map((fila) => (
      fila.clave !== clave ? fila : {
        ...fila,
        [campo]: valor,
        // Cambiar de panel invalida el puerto elegido.
        ...(campo === 'panel_id' ? { panel_port_id: '' } : {}),
        // Cambiar de switch invalida el puerto de switch elegido.
        ...(campo === 'switch_id' ? { switch_port_id: '' } : {}),
      }
    )));
    if (campo === 'panel_id') cargarPuertos(valor);
    if (campo === 'switch_id') cargarPuertosSwitch(valor);
    setFallos((prev) => {
      if (!prev[clave]) return prev;
      const siguiente = { ...prev };
      delete siguiente[clave];
      return siguiente;
    });
  };

  const anadirFila = () => setFilas((prev) => [...prev, { clave: nuevaClave(), ...FILA_VACIA }]);

  const borrarFila = (clave) => setFilas((prev) => (
    prev.length === 1 ? prev : prev.filter((f) => f.clave !== clave)
  ));

  const aplicarSemilla = () => {
    // Con un único switch elegido se aplica también a las filas; con varios
    // no se toca la columna Switch para no repartirlos a ciegas.
    const switchUnico = semilla.switch_ids.length === 1 ? semilla.switch_ids[0] : '';
    setFilas((prev) => prev.map((fila) => ({
      ...fila,
      location_id: semilla.location_id,
      panel_id: semilla.panel_id,
      panel_port_id: String(fila.panel_id) === String(semilla.panel_id) ? fila.panel_port_id : '',
      ...(switchUnico ? {
        switch_id: switchUnico,
        switch_port_id: String(fila.switch_id) === String(switchUnico) ? fila.switch_port_id : '',
      } : {}),
      outlet_type: semilla.outlet_type,
      status: semilla.status,
    })));
    cargarPuertos(semilla.panel_id);
    cargarPuertosSwitch(switchUnico);
  };

  /**
   * Rellena la tabla con códigos consecutivos y, si hay panel/switch en la
   * semilla, con sus puertos libres en orden (toma 1 → puerto 1, toma 2 →
   * puerto 2…). Los puertos ya ocupados se saltan.
   */
  const generar = async () => {
    const cantidad = Math.max(1, Math.min(500, Number(gen.cantidad) || 0));
    const desde = Number(gen.desde) || 1;
    const digitos = Math.max(1, Math.min(6, Number(gen.digitos) || 1));
    const panelId = semilla.panel_id;
    const switchIds = semilla.switch_ids;

    const [listaPanel, listasSwitch] = await Promise.all([
      panelId ? cargarPuertos(panelId) : Promise.resolve([]),
      Promise.all(switchIds.map((id) => cargarPuertosSwitch(id))),
    ]);

    const desdePanel = Math.max(1, Number(gen.panel_desde) || 1);
    const desdeSwitch = Math.max(1, Number(gen.switch_desde) || 1);

    const disponiblesPanel = (listaPanel || [])
      .filter((p) => puertoPanelLibre(p) && Number(p.port_number) >= desdePanel)
      .sort((a, b) => Number(a.port_number) - Number(b.port_number));

    // Los switches se consumen en el orden en que se añadieron a la semilla.
    const disponiblesSwitch = [];
    switchIds.forEach((id, indice) => {
      (listasSwitch[indice] || [])
        .filter((p) => puertoSwitchLibre(p) && Number(p.port_number) >= desdeSwitch)
        .sort((a, b) => Number(a.port_number) - Number(b.port_number))
        .forEach((p) => disponiblesSwitch.push({ switch_id: id, puerto: p }));
    });

    const nuevas = [];
    for (let i = 0; i < cantidad; i += 1) {
      const puertoPanel = disponiblesPanel[i];
      // Un puerto de switch sin panel no sirve: la cadena empieza en la toma.
      const parSwitch = puertoPanel ? disponiblesSwitch[i] : null;
      nuevas.push({
        ...FILA_VACIA,
        clave: nuevaClave(),
        code: `${gen.prefijo}${String(desde + i).padStart(digitos, '0')}`,
        location_id: semilla.location_id,
        panel_id: panelId,
        panel_port_id: puertoPanel ? String(puertoPanel.id) : '',
        switch_id: parSwitch ? String(parSwitch.switch_id) : '',
        switch_port_id: parSwitch ? String(parSwitch.puerto.id) : '',
        outlet_type: semilla.outlet_type,
        status: semilla.status,
      });
    }

    setFilas(nuevas);
    setFallos({});
    setError('');

    const avisos = [];
    if (!panelId && switchIds.length) {
      avisos.push('Sin patch panel no se asignan puertos de switch (la cadena empieza en la toma).');
    }
    if (panelId && disponiblesPanel.length < cantidad) {
      avisos.push(
        `${disponiblesPanel.length} puerto(s) libres en el patch panel para ${cantidad} fila(s); el resto queda sin puerto.`
      );
    }
    if (panelId && switchIds.length && disponiblesSwitch.length < cantidad) {
      avisos.push(
        `${disponiblesSwitch.length} puerto(s) libres en los switches para ${cantidad} fila(s); el resto queda sin switch.`
      );
    }
    setAviso(avisos.join(' '));
  };

  /**
   * Ordena la tabla de verdad (no solo al pintar): si se ordenara en cada
   * render, escribir en una celda reordenaría la fila y se perdería el foco.
   */
  const ordenarPor = (key) => {
    const dir = orden && orden.key === key && orden.dir === 'asc' ? 'desc' : 'asc';
    const valor = (fila) => {
      switch (key) {
        case 'location_id': return nombreSede(fila.location_id);
        case 'panel_id': return nombrePanel(fila.panel_id);
        case 'panel_port_id': {
          const lista = puertos[fila.panel_id] || [];
          return etiquetaPuerto(lista.find((p) => String(p.id) === String(fila.panel_port_id)));
        }
        case 'switch_id': return nombreSwitch(fila.switch_id);
        case 'switch_port_id': {
          const lista = puertosSwitch[fila.switch_id] || [];
          return etiquetaPuerto(lista.find((p) => String(p.id) === String(fila.switch_port_id)));
        }
        case 'outlet_type': return tipoEtiqueta(fila.outlet_type);
        case 'status': return fila.status === 'active' ? 'Activa' : 'Inactiva';
        default: return fila[key] || '';
      }
    };
    const comparar = (a, b) => String(valor(a)).localeCompare(String(valor(b)), 'es', { numeric: true });
    setFilas((prev) => [...prev].sort((a, b) => (dir === 'asc' ? comparar(a, b) : comparar(b, a))));
    setOrden({ key, dir });
  };

  /** Puertos ya elegidos por otras filas de la remesa (para no ofrecerlos). */
  const usadosEnRemesa = useMemo(() => ({
    panel: new Set(filas.filter((f) => f.panel_port_id).map((f) => String(f.panel_port_id))),
    switch: new Set(filas.filter((f) => f.switch_port_id).map((f) => String(f.switch_port_id))),
  }), [filas]);

  const conCodigo = filas.filter((f) => f.code.trim());
  const sinCodigo = filas.length - conCodigo.length;
  const conSwitch = filas.filter((f) => f.switch_port_id).length;

  const crear = async () => {
    setError('');
    if (!conCodigo.length) {
      setError('Escribe al menos un código.');
      return;
    }
    // Duplicados y puertos ocupados dentro de la propia tabla: el backend
    // también lo detecta, pero así se marcan antes de enviar nada.
    const vistos = new Map();
    const vistosPanel = new Map();
    const vistosSwitch = new Map();
    const repetidos = {};
    conCodigo.forEach((fila) => {
      const clave = fila.code.trim().toLowerCase();
      if (vistos.has(clave)) repetidos[fila.clave] = 'Código repetido en la tabla';
      else vistos.set(clave, fila.clave);

      if (fila.panel_port_id) {
        if (vistosPanel.has(fila.panel_port_id)) {
          repetidos[fila.clave] = 'Puerto de patch panel repetido en la tabla';
        } else {
          vistosPanel.set(fila.panel_port_id, fila.clave);
        }
        const puerto = (puertos[fila.panel_id] || [])
          .find((p) => String(p.id) === String(fila.panel_port_id));
        const motivo = motivoOcupadoPanel(puerto);
        if (motivo) repetidos[fila.clave] = `Puerto de patch panel ocupado (${motivo})`;
      }

      if (fila.switch_port_id) {
        if (!fila.panel_port_id) {
          repetidos[fila.clave] = 'Para llegar al switch falta el puerto del patch panel';
        }
        if (vistosSwitch.has(fila.switch_port_id)) {
          repetidos[fila.clave] = 'Puerto de switch repetido en la tabla';
        } else {
          vistosSwitch.set(fila.switch_port_id, fila.clave);
        }
        const puerto = (puertosSwitch[fila.switch_id] || [])
          .find((p) => String(p.id) === String(fila.switch_port_id));
        const motivo = motivoOcupadoSwitch(puerto);
        if (motivo && !repetidos[fila.clave]) {
          repetidos[fila.clave] = `Puerto de switch ocupado (${motivo})`;
        }
      } else if (fila.switch_id) {
        repetidos[fila.clave] = 'Elige el puerto del switch o quita el switch';
      }
    });
    if (Object.keys(repetidos).length) {
      setFallos(repetidos);
      setError('Revisa las filas marcadas: hay códigos o puertos repetidos/ocupados.');
      return;
    }

    setSaving(true);
    try {
      const payload = conCodigo.map((fila) => ({
        code: fila.code.trim(),
        name: fila.name || null,
        room: fila.room || null,
        floor: fila.floor || null,
        location_id: fila.location_id || null,
        panel_id: fila.panel_id || null,
        panel_code: panelDeFila(fila)?.code || null,
        panel_port_id: fila.panel_port_id || null,
        switch_id: fila.switch_id || null,
        switch_port_id: fila.switch_port_id || null,
        outlet_type: fila.outlet_type,
        status: fila.status,
      }));

      const respuesta = await api.bulkCreateWallOutlets(payload, companyId);
      const creadas = respuesta.count ?? (respuesta.created || []).length;

      const problemas = {};
      (respuesta.failed || []).forEach((fallo) => {
        const fila = conCodigo[fallo.index];
        if (fila) problemas[fila.clave] = fallo.error;
      });

      if (Object.keys(problemas).length) {
        setFallos(problemas);
        // Se dejan en la tabla solo las que hay que corregir.
        setFilas((prev) => prev.filter((f) => problemas[f.clave] || !f.code.trim()));
        setError(`${creadas} creadas. ${Object.keys(problemas).length} con problemas: revisa las filas marcadas.`);
        onCreated?.(creadas, false);
      } else {
        onCreated?.(creadas, true);
      }
    } catch (e) {
      setError(e.message || 'No se pudieron crear las tomas');
    } finally {
      setSaving(false);
    }
  };

  const resumen = useMemo(() => {
    const partes = [`${filas.length} ${filas.length === 1 ? 'fila' : 'filas'}`];
    if (sinCodigo) partes.push(`${sinCodigo} sin código`);
    if (conSwitch) partes.push(`${conSwitch} al switch`);
    if (Object.keys(fallos).length) partes.push(`${Object.keys(fallos).length} con problemas`);
    return partes.join(' · ');
  }, [filas.length, sinCodigo, conSwitch, fallos]);

  return (
    <Dialog
      title={`Crear tomas en bloque${conCodigo.length ? ` · ${conCodigo.length}` : ''}`}
      icon={<RectangleStackIcon style={{ width: 20, height: 20 }} />}
      onClose={onClose}
      busy={saving}
      className="bulk-dialog"
      maxWidth="min(1680px, 97vw)"
      footer={(
        <>
          <span className="list-hint" style={{ marginRight: 'auto' }}>{resumen}</span>
          <button type="button" className="list-btn-secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button
            type="button"
            className="list-btn-primary"
            onClick={crear}
            disabled={saving || !conCodigo.length}
          >
            {saving ? 'Creando…' : `Crear ${conCodigo.length || ''} ${conCodigo.length === 1 ? 'toma' : 'tomas'}`.trim()}
          </button>
        </>
      )}
    >
      {/* ══ Semilla: lo que comparten todas las filas ══ */}
      <div className="bulk-field-row">
        <label className="bulk-field">
          <span>Sede</span>
          <select
            className="list-select"
            value={semilla.location_id}
            onChange={(e) => setSemilla((s) => ({ ...s, location_id: e.target.value }))}
          >
            <option value="">— Sin sede —</option>
            {locations.map((l) => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
          </select>
        </label>

        <label className="bulk-field">
          <span>Patch panel</span>
          <select
            className="list-select"
            value={semilla.panel_id}
            onChange={(e) => {
              setSemilla((s) => ({ ...s, panel_id: e.target.value }));
              cargarPuertos(e.target.value);
            }}
          >
            <option value="">— Sin patch panel —</option>
            {panels.map((p) => <option key={p.id} value={String(p.id)}>{p.name || p.code}</option>)}
          </select>
        </label>

        <div className="bulk-field bulk-field--switches">
          <span>Switches (opcional)</span>
          <div className="bulk-switch-picker">
            {semilla.switch_ids.map((id) => (
              <span key={id} className="bulk-chip">
                {nombreSwitch(id) || `#${id}`}
                <button
                  type="button"
                  onClick={() => setSemilla((s) => ({
                    ...s,
                    switch_ids: s.switch_ids.filter((x) => x !== id),
                  }))}
                  title="Quitar de la semilla"
                  aria-label={`Quitar ${nombreSwitch(id)}`}
                >
                  <XMarkIcon style={{ width: 12, height: 12 }} />
                </button>
              </span>
            ))}
            <select
              className="list-select"
              value=""
              onChange={(e) => {
                const id = e.target.value;
                if (!id) return;
                setSemilla((s) => ({ ...s, switch_ids: [...s.switch_ids, id] }));
                cargarPuertosSwitch(id);
              }}
            >
              <option value="">＋ Añadir switch…</option>
              {switches
                .filter((s) => !semilla.switch_ids.includes(String(s.id)))
                .map((s) => <option key={s.id} value={String(s.id)}>{s.name || s.code}</option>)}
            </select>
          </div>
        </div>

        <label className="bulk-field">
          <span>Tipo</span>
          <select
            className="list-select"
            value={semilla.outlet_type}
            onChange={(e) => setSemilla((s) => ({ ...s, outlet_type: e.target.value }))}
          >
            {OUTLET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>

        <label className="bulk-field">
          <span>Estado</span>
          <select
            className="list-select"
            value={semilla.status}
            onChange={(e) => setSemilla((s) => ({ ...s, status: e.target.value }))}
          >
            <option value="active">Activa</option>
            <option value="inactive">Inactiva</option>
          </select>
        </label>

        <button type="button" className="list-btn-secondary" onClick={aplicarSemilla}>
          Aplicar a todas las filas
        </button>
      </div>

      {/* ══ Generador de códigos y puertos ══ */}
      <div className="bulk-field-row" style={{ marginTop: 14 }}>
        <label className="bulk-field">
          <span>Prefijo</span>
          <input
            className="list-input"
            value={gen.prefijo}
            onChange={(e) => setGen((g) => ({ ...g, prefijo: e.target.value }))}
            placeholder="T-"
          />
        </label>
        <label className="bulk-field">
          <span>Desde código</span>
          <input
            className="list-input"
            type="number"
            min="0"
            value={gen.desde}
            onChange={(e) => setGen((g) => ({ ...g, desde: e.target.value }))}
          />
        </label>
        <label className="bulk-field">
          <span>Cantidad</span>
          <input
            className="list-input"
            type="number"
            min="1"
            max="500"
            value={gen.cantidad}
            onChange={(e) => setGen((g) => ({ ...g, cantidad: e.target.value }))}
          />
        </label>
        <label className="bulk-field">
          <span>Dígitos</span>
          <input
            className="list-input"
            type="number"
            min="1"
            max="6"
            value={gen.digitos}
            onChange={(e) => setGen((g) => ({ ...g, digitos: e.target.value }))}
          />
        </label>
        <label className="bulk-field">
          <span>Desde puerto panel</span>
          <input
            className="list-input"
            type="number"
            min="1"
            value={gen.panel_desde}
            onChange={(e) => setGen((g) => ({ ...g, panel_desde: e.target.value }))}
            title="Primer puerto libre del patch panel que se asigna a la primera fila"
          />
        </label>
        <label className="bulk-field">
          <span>Desde puerto switch</span>
          <input
            className="list-input"
            type="number"
            min="1"
            value={gen.switch_desde}
            onChange={(e) => setGen((g) => ({ ...g, switch_desde: e.target.value }))}
            title="Primer puerto libre de los switches que se asigna a la primera fila"
          />
        </label>
        <button type="button" className="list-btn-primary" onClick={generar}>
          Generar filas
        </button>
      </div>

      {aviso && <div className="alert alert-warning" style={{ marginTop: 10 }}>{aviso}</div>}
      {error && <div className="alert alert-danger" style={{ marginTop: 10 }}>{error}</div>}

      <p className="list-hint" style={{ margin: '12px 0 0' }}>
        Los puertos ya ocupados por otra toma o por un latiguillo no se pueden elegir.
        «En esta remesa» significa que ya los ha cogido otra fila.
      </p>

      {/* ══ Tabla editable ══ */}
      <div className="bulk-grid-wrap">
        <table className="bulk-grid">
          <thead>
            <tr>
              {COLUMNAS.map((col) => (
                <th
                  key={col.key}
                  className="sortable"
                  onClick={() => ordenarPor(col.key)}
                  title={`Ordenar por ${col.label.replace(' *', '')}`}
                >
                  {col.label}
                  {orden?.key === col.key && (
                    orden.dir === 'asc'
                      ? <ArrowUpIcon style={{ width: 12, height: 12, marginLeft: 4 }} />
                      : <ArrowDownIcon style={{ width: 12, height: 12, marginLeft: 4 }} />
                  )}
                </th>
              ))}
              <th aria-label="Quitar" />
            </tr>
          </thead>
          <tbody>
            {filas.map((fila) => {
              const listaPuertos = puertos[fila.panel_id] || [];
              const listaPuertosSwitch = puertosSwitch[fila.switch_id] || [];
              return (
                <tr key={fila.clave} className={fallos[fila.clave] ? 'is-error' : undefined}>
                  <td>
                    <input
                      className="list-input"
                      value={fila.code}
                      onChange={(e) => actualizar(fila.clave, 'code', e.target.value)}
                      placeholder="T-01"
                    />
                    {fallos[fila.clave] && <p className="bulk-grid-error">{fallos[fila.clave]}</p>}
                  </td>
                  <td>
                    <input
                      className="list-input"
                      value={fila.name}
                      onChange={(e) => actualizar(fila.clave, 'name', e.target.value)}
                      placeholder="Puesto 1"
                    />
                  </td>
                  <td>
                    <input
                      className="list-input"
                      value={fila.room}
                      onChange={(e) => actualizar(fila.clave, 'room', e.target.value)}
                      placeholder="Sala"
                    />
                  </td>
                  <td>
                    <input
                      className="list-input"
                      value={fila.floor}
                      onChange={(e) => actualizar(fila.clave, 'floor', e.target.value)}
                      placeholder="Planta"
                    />
                  </td>
                  <td>
                    <select
                      className="list-select"
                      value={fila.location_id}
                      onChange={(e) => actualizar(fila.clave, 'location_id', e.target.value)}
                    >
                      <option value="">—</option>
                      {locations.map((l) => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select
                      className="list-select"
                      value={fila.panel_id}
                      onChange={(e) => actualizar(fila.clave, 'panel_id', e.target.value)}
                    >
                      <option value="">—</option>
                      {panels.map((p) => <option key={p.id} value={String(p.id)}>{p.name || p.code}</option>)}
                    </select>
                  </td>
                  <td>
                    <select
                      className="list-select"
                      value={fila.panel_port_id}
                      onChange={(e) => actualizar(fila.clave, 'panel_port_id', e.target.value)}
                      disabled={!fila.panel_id}
                      title={fila.panel_id ? 'Puerto del patch panel' : 'Elige antes el patch panel'}
                    >
                      <option value="">—</option>
                      {listaPuertos.map((p) => {
                        const motivo = motivoOcupadoPanel(p);
                        const enRemesa = usadosEnRemesa.panel.has(String(p.id))
                          && String(fila.panel_port_id) !== String(p.id);
                        return (
                          <option key={p.id} value={String(p.id)} disabled={Boolean(motivo) || enRemesa}>
                            {etiquetaPuerto(p)}
                            {motivo ? ` · ${motivo}` : (enRemesa ? ' · en esta remesa' : '')}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                  <td>
                    <select
                      className="list-select"
                      value={fila.switch_id}
                      onChange={(e) => actualizar(fila.clave, 'switch_id', e.target.value)}
                      title="Switch al que llega el latiguillo (opcional)"
                    >
                      <option value="">—</option>
                      {switches.map((s) => (
                        <option key={s.id} value={String(s.id)}>{s.name || s.code}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="list-select"
                      value={fila.switch_port_id}
                      onChange={(e) => actualizar(fila.clave, 'switch_port_id', e.target.value)}
                      disabled={!fila.switch_id || !fila.panel_port_id}
                      title={
                        !fila.switch_id
                          ? 'Elige antes el switch'
                          : (!fila.panel_port_id
                            ? 'Elige antes el puerto del patch panel'
                            : 'Puerto libre del switch')
                      }
                    >
                      <option value="">—</option>
                      {listaPuertosSwitch.map((p) => {
                        const motivo = motivoOcupadoSwitch(p);
                        const enRemesa = usadosEnRemesa.switch.has(String(p.id))
                          && String(fila.switch_port_id) !== String(p.id);
                        return (
                          <option key={p.id} value={String(p.id)} disabled={Boolean(motivo) || enRemesa}>
                            {etiquetaPuerto(p)}
                            {motivo ? ` · ${motivo}` : (enRemesa ? ' · en esta remesa' : '')}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                  <td>
                    <select
                      className="list-select"
                      value={fila.outlet_type}
                      onChange={(e) => actualizar(fila.clave, 'outlet_type', e.target.value)}
                    >
                      {OUTLET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <select
                      className="list-select"
                      value={fila.status}
                      onChange={(e) => actualizar(fila.clave, 'status', e.target.value)}
                    >
                      <option value="active">Activa</option>
                      <option value="inactive">Inactiva</option>
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="list-action-btn list-action-btn--danger"
                      onClick={() => borrarFila(fila.clave)}
                      disabled={filas.length === 1}
                      title="Quitar esta fila"
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button type="button" className="list-btn-secondary" onClick={anadirFila} style={{ marginTop: 10 }}>
        <PlusIcon style={{ width: 14, height: 14 }} /> Añadir fila
      </button>
    </Dialog>
  );
}

export default WallOutletsBulkDialog;
